import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { apiConnectorGet, apiConnectorPost } from "../services/apiconnector";
import { endpoint } from "../services/urls";
import { EyeO, ClosedEye } from "@react-vant/icons";

export default function MyProfile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    wallet_address: "",
    pin: "",
  });
  const [focused, setFocused] = useState("");
  const [nomineeEditOpen, setNomineeEditOpen] = useState(false);
  const [nomineeSaving, setNomineeSaving] = useState(false);
  const [nomineeForm, setNomineeForm] = useState({
    name: "",
    mobile: "",
    email: "",
    address: "",
  });
  const [nomineeFocused, setNomineeFocused] = useState("");
  const [pinDigits, setPinDigits] = useState(["", "", "", "", ""]);
  const [pinFocused, setPinFocused] = useState(null);
  const [showPin, setShowPin] = useState(false);
  const pinRefs = useRef([]);

  const digitsFromPin = (pin) => {
    const chars = String(pin || "")
      .split("")
      .slice(0, 5);
    return [0, 1, 2, 3, 4].map((i) => chars[i] || "");
  };

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const response = await apiConnectorGet(endpoint.profile_detail);
      if (response?.data?.success && response.data.result?.length > 0) {
        const p = response.data.result[0];
        setProfile(p);
        setForm({
          name: p.lgn_name || "",
          mobile: p.lgn_mobile || "",
          email: p.lgn_email || "",
          wallet_address: p.lgn_wallet_add || "",
          pin: p.lgn_pin || "",
        });
        setPinDigits(digitsFromPin(p.lgn_pin));
        setNomineeForm({
          name: p.n01_name || "",
          mobile: p.n01_mob || "",
          email: p.n01_email || "",
          address: p.n01_address || "",
        });
      } else {
        toast.error(response?.data?.message || "Failed to fetch profile");
      }
    } catch {
      toast.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Full name is required");
    if (!form.mobile.trim()) return toast.error("Mobile is required");
    if (!form.email.trim()) return toast.error("Email is required");
    if (form.pin.length !== 5) return toast.error("Enter a 5-digit PIN");
    setSaving(true);
    try {
      const response = await apiConnectorPost(endpoint.update_profile, {
        name: form.name.trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim(),
        wallet_address: form.wallet_address.trim(),
        lgn_pin: form.pin,
      });
      if (response?.data?.success) {
        toast.success(response.data.message || "Profile updated");
        setEditOpen(false);
        fetchProfileData();
      } else {
        toast.error(response?.data?.message || "Update failed");
      }
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      name: profile?.lgn_name || "",
      mobile: profile?.lgn_mobile || "",
      email: profile?.lgn_email || "",
      wallet_address: profile?.lgn_wallet_add || "",
      pin: profile?.lgn_pin || "",
    });
    setPinDigits(digitsFromPin(profile?.lgn_pin));
    setEditOpen(false);
  };

  const handleNomineeSave = async () => {
    if (!nomineeForm.name.trim())
      return toast.error("Nominee name is required");
    setNomineeSaving(true);
    try {
      const response = await apiConnectorPost(endpoint.update_nominee_details, {
        name: nomineeForm.name.trim(),
        mobile: nomineeForm.mobile.trim(),
        email: nomineeForm.email.trim(),
        address: nomineeForm.address.trim(),
      });
      if (response?.data?.success) {
        toast.success(response.data.message || "Nominee details updated");
        setNomineeEditOpen(false);
        fetchProfileData();
      } else {
        toast.error(response?.data?.message || "Update failed");
      }
    } catch {
      toast.error("Update failed");
    } finally {
      setNomineeSaving(false);
    }
  };

  const handleNomineeCancel = () => {
    setNomineeForm({
      name: profile?.n01_name || "",
      mobile: profile?.n01_mob || "",
      email: profile?.n01_email || "",
      address: profile?.n01_address || "",
    });
    setNomineeEditOpen(false);
  };

  const handlePinChange = (index, e) => {
    const val = e.target.value.replace(/\D/g, "").slice(-1);
    const next = [...pinDigits];
    next[index] = val;
    setPinDigits(next);
    setForm((f) => ({ ...f, pin: next.join("") }));
    if (val && index < 4) pinRefs.current[index + 1]?.focus();
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === "Backspace" && !pinDigits[index] && index > 0) {
      pinRefs.current[index - 1]?.focus();
    }
  };

  const handlePinPaste = (e) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 5);
    if (!text) return;
    e.preventDefault();
    const next = digitsFromPin(text);
    setPinDigits(next);
    setForm((f) => ({ ...f, pin: next.join("") }));
    pinRefs.current[Math.min(text.length, 4)]?.focus();
  };

  const copy = (text, field) => {
    if (!text || text === "N/A" || text === "Not Set") return;
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success("Copied!");
    setTimeout(() => setCopiedField(null), 1800);
  };

  const formatDate = (d) => {
    if (!d) return "N/A";
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const initials = (name) => {
    if (!name) return "U";
    return name
      .trim()
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("type");
    navigate("/");
    window.location.reload();
  };

  if (loading) {
    return (
      <div style={s.loaderWrap}>
        <div style={s.spinner} />
        <p style={s.loaderText}>Loading profile…</p>
      </div>
    );
  }

  const isActive = profile?.lgn_is_blocked === "No";
  const name = profile?.lgn_name || "User";
  const hasNomineeDetails = Boolean(
    profile?.n01_name &&
    profile?.n01_mob &&
    profile?.n01_email &&
    profile?.n01_address,
  );

  return (
    <div style={s.root}>
      <div style={s.grid} />
      <div style={s.glowTL} />
      <div style={s.glowBR} />

      <div style={s.container}>
        {/* ── Hero Card ── */}
        <div style={s.heroCard}>
          <div style={s.heroBanner} />
          <div style={s.heroBody}>
            <div style={s.avatarWrap}>
              <div style={s.avatar}>{initials(name)}</div>
              <div
                style={{
                  ...s.statusDot,
                  background: isActive ? "#00e676" : "#ff5252",
                }}
              />
            </div>
            <h2 style={s.heroName}>{name}</h2>
            <p style={s.heroId}>ID: {profile?.lgn_cust_id || "N/A"}</p>
            <span
              style={{
                ...s.badge,
                ...(isActive ? s.badgeActive : s.badgeBlocked),
              }}
            >
              {isActive ? "● Active" : "● Blocked"}
            </span>
          </div>
        </div>

        {/* ── Stats Row ── */}
        <div style={s.statsRow}>
          <StatBox
            label="Rank"
            value={profile?.is_bot_bought ? "D" : "N/A"}
            icon="🏅"
          />
          <StatBox
            label="Member Since"
            value={formatDate(profile?.tr03_reg_date)}
            icon="📅"
          />
          <StatBox
            label="Income Active"
            value={profile?.tr03_active_for_inc === 1 ? "Yes" : "No"}
            icon="💰"
            accent={profile?.tr03_active_for_inc === 1}
          />
        </div>

        {/* ── Referral Card ── */}
        <div style={s.referralCard}>
          <p style={s.referralLabel}>Your Referral ID</p>
          <div style={s.referralRow}>
            <span style={s.referralValue}>{profile?.lgn_cust_id || "N/A"}</span>
            <button
              style={s.copyBtn}
              onClick={() => copy(profile?.lgn_cust_id, "referral")}
            >
              {copiedField === "referral" ? "✓ Copied" : "Copy"}
            </button>
          </div>
          <p style={s.referralHint}>
            Share your ID to invite friends and earn rewards
          </p>
        </div>
        {/* ── Sponsor ── */}
        <Section title="Sponsor" icon="⭐">
          {/* <InfoItem label="Sponsor Name" value={profile?.spon_name || "N/A"} icon="👥" /> */}
          <InfoItem
            label="Sponsor ID"
            value={profile?.spon_id || "N/A"}
            icon="🔗"
          />
        </Section>
        {/* ── Contact Info — inline editable ── */}
        <div style={s.section}>
          <div style={s.sectionHeader}>
            <span style={s.sectionIcon}>👤</span>
            <span style={{ ...s.sectionTitle, flex: 1 }}>Contact Info</span>
            <button
              style={{
                ...s.editIconBtn,
                ...(editOpen ? s.editIconBtnActive : {}),
              }}
              onClick={() => (editOpen ? handleCancel() : setEditOpen(true))}
              title={editOpen ? "Cancel" : "Edit"}
            >
              {editOpen ? "✕" : "✏️"}
            </button>
          </div>

          {/* View mode */}
          {!editOpen && (
            <>
              <InfoItem label="Full Name" value={profile?.lgn_name} icon="🙍" />
              <InfoItem
                label="Email"
                value={profile?.lgn_email}
                icon="✉️"
                onCopy={() => copy(profile?.lgn_email, "email")}
                copied={copiedField === "email"}
              />
              <InfoItem
                label="Mobile"
                value={profile?.lgn_mobile}
                icon="📱"
                onCopy={() => copy(profile?.lgn_mobile, "mobile")}
                copied={copiedField === "mobile"}
              />
              <InfoItem
                label="Wallet Address"
                value={profile?.lgn_wallet_add || "Not Set"}
                icon="💳"
                onCopy={
                  profile?.lgn_wallet_add
                    ? () => copy(profile.lgn_wallet_add, "wallet")
                    : null
                }
                copied={copiedField === "wallet"}
                truncate
              />
            </>
          )}

          {/* Edit mode */}
          {editOpen && (
            <div style={s.editBody}>
              <InlineField
                label="Full Name"
                icon="🙍"
                value={form.name}
                placeholder="Enter your full name"
                focused={focused === "name"}
                onFocus={() => setFocused("name")}
                onBlur={() => setFocused("")}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <InlineField
                label="Mobile"
                icon="📱"
                value={form.mobile}
                placeholder="Enter your mobile number"
                type="tel"
                focused={focused === "mobile"}
                onFocus={() => setFocused("mobile")}
                onBlur={() => setFocused("")}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              />
              <InlineField
                label="Email"
                icon="✉️"
                value={form.email}
                placeholder="Enter your email"
                type="email"
                focused={focused === "email"}
                onFocus={() => setFocused("email")}
                onBlur={() => setFocused("")}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <InlineField
                label="Wallet Address"
                icon="💳"
                value={form.wallet_address}
                placeholder="Enter your wallet address"
                focused={focused === "wallet_address"}
                onFocus={() => setFocused("wallet_address")}
                onBlur={() => setFocused("")}
                onChange={(e) =>
                  setForm({ ...form, wallet_address: e.target.value })
                }
              />
              <div style={ef.wrap}>
                <label style={ef.label}>Login PIN</label>
                <div style={ef.pinRow} onPaste={handlePinPaste}>
                  {pinDigits.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (pinRefs.current[i] = el)}
                      value={digit}
                      onChange={(e) => handlePinChange(i, e)}
                      onKeyDown={(e) => handlePinKeyDown(i, e)}
                      onFocus={() => setPinFocused(i)}
                      onBlur={() => setPinFocused(null)}
                      type={showPin ? "text" : "password"}
                      inputMode="numeric"
                      maxLength={1}
                      autoComplete="off"
                      style={{
                        ...ef.pinBox,
                        ...(pinFocused === i ? ef.pinBoxActive : {}),
                      }}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => setShowPin((v) => !v)}
                    style={ef.pinEyeBtn}
                    title={showPin ? "Hide PIN" : "Show PIN"}
                  >
                    {showPin ? <ClosedEye /> : <EyeO />}
                  </button>
                </div>
              </div>
              <div style={s.editActions}>
                <button
                  style={s.cancelBtn}
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  style={{ ...s.saveBtn, opacity: saving ? 0.6 : 1 }}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span style={s.savingSpinner} />
                      Saving…
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Nominee Details — inline editable ── */}
        <div style={s.section}>
          <div style={s.sectionHeader}>
            <span style={s.sectionIcon}>🧾</span>
            <span style={{ ...s.sectionTitle, flex: 1 }}>Nominee Details</span>
            {!hasNomineeDetails && (
              <button
                style={{
                  ...s.editIconBtn,
                  ...(nomineeEditOpen ? s.editIconBtnActive : {}),
                }}
                onClick={() =>
                  nomineeEditOpen
                    ? handleNomineeCancel()
                    : setNomineeEditOpen(true)
                }
                title={nomineeEditOpen ? "Cancel" : "Update Nominee Details"}
              >
                {nomineeEditOpen ? "✕" : "✏️"}
              </button>
            )}
          </div>

          {/* View mode */}
          {!nomineeEditOpen && (
            <>
              <InfoItem
                label="Nominee Name"
                value={profile?.n01_name}
                icon="🙍"
              />
              <InfoItem
                label="Nominee Mobile"
                value={profile?.n01_mob}
                icon="📱"
              />
              <InfoItem
                label="Nominee Email"
                value={profile?.n01_email}
                icon="✉️"
              />
              <InfoItem
                label="Nominee Address"
                value={profile?.n01_address}
                icon="🏠"
              />
            </>
          )}

          {/* Edit mode */}
          {nomineeEditOpen && (
            <div style={s.editBody}>
              <InlineField
                label="Nominee Name"
                icon="🙍"
                value={nomineeForm.name}
                placeholder="Enter nominee's full name"
                focused={nomineeFocused === "name"}
                onFocus={() => setNomineeFocused("name")}
                onBlur={() => setNomineeFocused("")}
                onChange={(e) =>
                  setNomineeForm({ ...nomineeForm, name: e.target.value })
                }
              />
              <InlineField
                label="Nominee Mobile"
                icon="📱"
                value={nomineeForm.mobile}
                placeholder="Enter nominee's mobile number"
                type="tel"
                focused={nomineeFocused === "mobile"}
                onFocus={() => setNomineeFocused("mobile")}
                onBlur={() => setNomineeFocused("")}
                onChange={(e) =>
                  setNomineeForm({ ...nomineeForm, mobile: e.target.value })
                }
              />
              <InlineField
                label="Nominee Email"
                icon="✉️"
                value={nomineeForm.email}
                placeholder="Enter nominee's email"
                type="email"
                focused={nomineeFocused === "email"}
                onFocus={() => setNomineeFocused("email")}
                onBlur={() => setNomineeFocused("")}
                onChange={(e) =>
                  setNomineeForm({ ...nomineeForm, email: e.target.value })
                }
              />
              <InlineField
                label="Nominee Address"
                icon="🏠"
                value={nomineeForm.address}
                placeholder="Enter nominee's address"
                focused={nomineeFocused === "address"}
                onFocus={() => setNomineeFocused("address")}
                onBlur={() => setNomineeFocused("")}
                onChange={(e) =>
                  setNomineeForm({ ...nomineeForm, address: e.target.value })
                }
              />
              <div style={s.editActions}>
                <button
                  style={s.cancelBtn}
                  onClick={handleNomineeCancel}
                  disabled={nomineeSaving}
                >
                  Cancel
                </button>
                <button
                  style={{ ...s.saveBtn, opacity: nomineeSaving ? 0.6 : 1 }}
                  onClick={handleNomineeSave}
                  disabled={nomineeSaving}
                >
                  {nomineeSaving ? (
                    <>
                      <span style={s.savingSpinner} />
                      Saving…
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Account Details ── */}
        {/* <Section title="Account Details" icon="🔐">
          <InfoItem label="Login ID"  value={profile?.tr03_lgn_id}    icon="🪪" />
          <InfoItem label="User Type" value={profile?.lgn_user_type}  icon="🏷️" />
        </Section> */}

        {/* ── Activity ── */}
        {/* <Section title="Activity" icon="📊">
          <InfoItem label="Topup Date" value={formatDate(profile?.tr03_topup_date)} icon="💼" />
          <InfoItem label="Rank Date"  value={formatDate(profile?.tr03_rank_date)}  icon="🏆" />
        </Section> */}

        {/* ── Logout ── */}
        <button style={s.logoutBtn} onClick={handleLogout}>
          <span>🚪</span> Sign Out
        </button>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Sora:wght@300;400;500;600;700&display=swap');
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes fadeUp  { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
        @keyframes fadeIn  { from { opacity:0; } to { opacity:1; } }
      `}</style>
    </div>
  );
}

// ── Inline edit field ─────────────────────────────────────────────────────────
function InlineField({
  label,
  icon,
  value,
  placeholder,
  type = "text",
  focused,
  onFocus,
  onBlur,
  onChange,
}) {
  return (
    <div style={ef.wrap}>
      <label style={ef.label}>{label}</label>
      <div style={{ ...ef.row, ...(focused ? ef.rowActive : {}) }}>
        <span style={ef.icon}>{icon}</span>
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          autoComplete="off"
          style={ef.input}
        />
      </div>
    </div>
  );
}

// ── Generic Section ───────────────────────────────────────────────────────────
function Section({ title, icon, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionHeader}>
        <span style={s.sectionIcon}>{icon}</span>
        <span style={s.sectionTitle}>{title}</span>
      </div>
      <div>{children}</div>
    </div>
  );
}

// ── Info row ──────────────────────────────────────────────────────────────────
function InfoItem({ label, value, icon, onCopy, copied, truncate }) {
  return (
    <div style={s.infoItem}>
      <div style={s.infoLeft}>
        <span style={s.infoIcon}>{icon}</span>
        <div style={{ minWidth: 0 }}>
          <p style={s.infoLabel}>{label}</p>
          <p style={{ ...s.infoValue, ...(truncate ? s.truncate : {}) }}>
            {value || "N/A"}
          </p>
        </div>
      </div>
      {onCopy && (
        <button
          style={{ ...s.inlineCopy, ...(copied ? s.inlineCopied : {}) }}
          onClick={onCopy}
        >
          {copied ? "✓" : "⧉"}
        </button>
      )}
    </div>
  );
}

// ── Stat box ──────────────────────────────────────────────────────────────────
function StatBox({ label, value, icon, accent }) {
  return (
    <div style={s.statBox}>
      <span style={s.statIcon}>{icon}</span>
      <p style={{ ...s.statValue, ...(accent ? { color: "#00e5ff" } : {}) }}>
        {value}
      </p>
      <p style={s.statLabel}>{label}</p>
    </div>
  );
}

// ── Palette ───────────────────────────────────────────────────────────────────
const C = {
  bg: "#020c12",
  card: "rgba(4,16,24,0.90)",
  border: "rgba(0,229,255,0.10)",
  cyan: "#00e5ff",
  cyanDim: "rgba(0,229,255,0.4)",
  text: "#e0f7fa",
  muted: "rgba(100,180,200,0.5)",
};

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  root: {
    minHeight: "100vh",
    background: C.bg,
    fontFamily: "'Sora',sans-serif",
    position: "relative",
    overflow: "hidden",
    paddingBottom: 90,
  },
  grid: {
    position: "fixed",
    inset: 0,
    pointerEvents: "none",
    backgroundImage:
      "linear-gradient(rgba(0,229,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,229,255,0.03) 1px,transparent 1px)",
    backgroundSize: "44px 44px",
  },
  glowTL: {
    position: "fixed",
    top: -150,
    left: -100,
    width: 480,
    height: 480,
    borderRadius: "50%",
    pointerEvents: "none",
    background:
      "radial-gradient(circle,rgba(0,229,255,0.08) 0%,transparent 70%)",
  },
  glowBR: {
    position: "fixed",
    bottom: -100,
    right: -80,
    width: 380,
    height: 380,
    borderRadius: "50%",
    pointerEvents: "none",
    background:
      "radial-gradient(circle,rgba(0,180,200,0.05) 0%,transparent 70%)",
  },
  container: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "24px 16px 40px",
    position: "relative",
    zIndex: 10,
    animation: "fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both",
  },

  // loader
  loaderWrap: {
    minHeight: "100vh",
    background: C.bg,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
  },
  spinner: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    border: "3px solid rgba(0,229,255,0.15)",
    borderTopColor: C.cyan,
    animation: "spin 0.75s linear infinite",
  },
  loaderText: {
    color: C.muted,
    fontSize: 12,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.15em",
  },

  // hero
  heroCard: {
    borderRadius: 20,
    overflow: "hidden",
    border: `1px solid ${C.border}`,
    background: C.card,
    marginBottom: 14,
    backdropFilter: "blur(20px)",
    boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
  },
  heroBanner: {
    height: 72,
    background: "linear-gradient(135deg,#001a22 0%,#00303f 50%,#001a22 100%)",
    borderBottom: `1px solid ${C.border}`,
  },
  heroBody: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0 24px 28px",
  },
  avatarWrap: { position: "relative", marginTop: -38 },
  avatar: {
    width: 76,
    height: 76,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#00e5ff,#0077aa)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 26,
    fontWeight: 700,
    color: "#020c12",
    border: "3px solid #020c12",
    boxShadow: "0 0 0 2px rgba(0,229,255,0.35),0 8px 24px rgba(0,229,255,0.2)",
    fontFamily: "'Space Mono',monospace",
  },
  statusDot: {
    position: "absolute",
    bottom: 4,
    right: 4,
    width: 14,
    height: 14,
    borderRadius: "50%",
    border: "2px solid #020c12",
    boxShadow: "0 0 6px rgba(0,230,118,0.6)",
  },
  heroName: {
    marginTop: 12,
    fontSize: 20,
    fontWeight: 700,
    color: C.text,
    letterSpacing: "-0.02em",
  },
  heroId: {
    fontSize: 11,
    color: C.muted,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.06em",
    marginTop: 4,
  },
  badge: {
    marginTop: 10,
    fontSize: 10,
    fontWeight: 600,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.1em",
    padding: "4px 12px",
    borderRadius: 20,
  },
  badgeActive: {
    background: "rgba(0,230,118,0.1)",
    color: "#00e676",
    border: "1px solid rgba(0,230,118,0.25)",
  },
  badgeBlocked: {
    background: "rgba(255,82,82,0.1)",
    color: "#ff5252",
    border: "1px solid rgba(255,82,82,0.25)",
  },

  // stats
  statsRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 10,
    marginBottom: 14,
  },
  statBox: {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: "14px 10px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    backdropFilter: "blur(20px)",
  },
  statIcon: { fontSize: 18 },
  statValue: {
    fontSize: 13,
    fontWeight: 700,
    color: C.text,
    fontFamily: "'Space Mono',monospace",
    textAlign: "center",
  },
  statLabel: {
    fontSize: 9,
    color: C.muted,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    textAlign: "center",
  },

  // referral
  referralCard: {
    background:
      "linear-gradient(135deg,rgba(0,229,255,0.06) 0%,rgba(0,180,200,0.03) 100%)",
    border: "1px solid rgba(0,229,255,0.2)",
    borderRadius: 16,
    padding: "18px 20px",
    marginBottom: 14,
  },
  referralLabel: {
    fontSize: 9,
    color: C.cyanDim,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    marginBottom: 10,
  },
  referralRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  referralValue: {
    fontSize: 18,
    fontWeight: 700,
    color: C.cyan,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.08em",
  },
  copyBtn: {
    background: "rgba(0,229,255,0.1)",
    border: "1px solid rgba(0,229,255,0.25)",
    color: C.cyan,
    borderRadius: 8,
    padding: "6px 16px",
    fontSize: 11,
    fontFamily: "'Space Mono',monospace",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.08em",
  },
  referralHint: {
    fontSize: 10,
    color: C.muted,
    marginTop: 10,
    lineHeight: 1.5,
  },

  // section
  section: {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    overflow: "hidden",
    backdropFilter: "blur(20px)",
    marginBottom: 12,
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "14px 18px 12px",
    borderBottom: `1px solid ${C.border}`,
    background: "rgba(0,229,255,0.025)",
  },
  sectionIcon: { fontSize: 16 },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: C.text,
    letterSpacing: "-0.01em",
  },

  // edit icon button
  editIconBtn: {
    marginLeft: "auto",
    width: 30,
    height: 30,
    borderRadius: 8,
    border: "1px solid rgba(0,229,255,0.18)",
    background: "rgba(0,229,255,0.06)",
    color: C.cyanDim,
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.18s",
    flexShrink: 0,
  },
  editIconBtnActive: {
    background: "rgba(255,82,82,0.08)",
    border: "1px solid rgba(255,82,82,0.25)",
    color: "rgba(255,100,100,0.8)",
  },

  // edit form
  editBody: {
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  editActions: { display: "flex", gap: 10, marginTop: 4 },
  cancelBtn: {
    flex: 1,
    height: 42,
    borderRadius: 10,
    border: "1px solid rgba(0,229,255,0.14)",
    background: "rgba(0,229,255,0.04)",
    color: "rgba(0,229,255,0.5)",
    fontFamily: "'Space Mono',monospace",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.08em",
    cursor: "pointer",
  },
  saveBtn: {
    flex: 2,
    height: 42,
    borderRadius: 10,
    border: "none",
    background: "linear-gradient(90deg,#00e5ff,#00b8d4)",
    color: "#020c12",
    fontFamily: "'Space Mono',monospace",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.1em",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    boxShadow: "0 4px 16px rgba(0,229,255,0.22)",
  },
  savingSpinner: {
    width: 13,
    height: 13,
    borderRadius: "50%",
    border: "2px solid rgba(2,12,18,0.2)",
    borderTopColor: "#020c12",
    animation: "spin 0.7s linear infinite",
    display: "inline-block",
  },

  // info item
  infoItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "13px 18px",
    borderBottom: "1px solid rgba(0,229,255,0.05)",
  },
  infoLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: 1,
    minWidth: 0,
  },
  infoIcon: { fontSize: 16, flexShrink: 0 },
  infoLabel: {
    fontSize: 9,
    color: C.muted,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: 2,
  },
  infoValue: { fontSize: 13, color: C.text, fontWeight: 500 },
  truncate: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 180,
  },
  inlineCopy: {
    flexShrink: 0,
    background: "rgba(0,229,255,0.07)",
    border: "1px solid rgba(0,229,255,0.18)",
    color: C.cyanDim,
    borderRadius: 8,
    width: 30,
    height: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    cursor: "pointer",
  },
  inlineCopied: {
    color: "#00e676",
    borderColor: "rgba(0,230,118,0.3)",
    background: "rgba(0,230,118,0.07)",
  },

  // logout
  logoutBtn: {
    width: "100%",
    marginTop: 8,
    height: 50,
    borderRadius: 14,
    border: "1px solid rgba(255,82,82,0.2)",
    background: "rgba(255,82,82,0.05)",
    color: "rgba(255,100,100,0.75)",
    fontFamily: "'Space Mono',monospace",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
};

// ── Inline field styles ───────────────────────────────────────────────────────
const ef = {
  wrap: { display: "flex", flexDirection: "column", gap: 5 },
  label: {
    fontSize: 9,
    color: "rgba(0,229,255,0.4)",
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "rgba(0,229,255,0.025)",
    border: "1px solid rgba(0,229,255,0.11)",
    borderRadius: 10,
    padding: "0 12px",
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  rowActive: {
    borderColor: "rgba(0,229,255,0.45)",
    boxShadow: "0 0 0 3px rgba(0,229,255,0.06)",
  },
  icon: { fontSize: 14, flexShrink: 0 },
  input: {
    flex: 1,
    background: "transparent",
    color: "#e0f7fa",
    fontSize: 13,
    fontFamily: "'Sora',sans-serif",
    padding: "12px 0",
    outline: "none",
    border: "none",
    WebkitBoxShadow: "0 0 0 1000px transparent inset",
    WebkitTextFillColor: "#e0f7fa",
  },
  pinRow: { display: "flex", alignItems: "center", gap: 8 },
  pinBox: {
    width: "100%",
    maxWidth: 40,
    height: 42,
    flex: 1,
    minWidth: 0,
    textAlign: "center",
    background: "rgba(0,229,255,0.025)",
    border: "1px solid rgba(0,229,255,0.11)",
    borderRadius: 10,
    color: "#e0f7fa",
    fontSize: 16,
    fontFamily: "'Sora',sans-serif",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  pinBoxActive: {
    borderColor: "rgba(0,229,255,0.45)",
    boxShadow: "0 0 0 3px rgba(0,229,255,0.06)",
  },
  pinEyeBtn: {
    flexShrink: 0,
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "rgba(0,229,255,0.3)",
    display: "flex",
    alignItems: "center",
    padding: 4,
    transition: "color 0.15s",
  },
};
